import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import URL from '@core/scripts/helper/url';
import Logger from '@core/scripts/components/logger';

const OWA_API_URL = '/ajax/marketing-parameters/';
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RETRY_INTERVAL_MS = 850;

/**
 * Service class for interacting with Optimus Marketing API.
 * Handles retry logic and polling internally.
 */
class OptimusService {
    constructor() {
        this.attemptCount = 0;
        this.maxAttempts = DEFAULT_MAX_ATTEMPTS;
        this.retryInterval = DEFAULT_RETRY_INTERVAL_MS;
        this.intervalId = null;
        this.pendingPromise = null;
        this.latestResult = null;
        this.isFetching = false;
    }

    /**
     * Resets the attempt counter.
     */
    resetAttempts() {
        this.attemptCount = 0;
    }

    /**
     * Checks if max attempts have been reached.
     *
     * @returns {boolean} True if max attempts reached
     */
    hasReachedMaxAttempts() {
        return this.attemptCount >= this.maxAttempts;
    }

    /**
     * Stops any active polling interval.
     */
    stopPolling() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Fetches marketing parameters from the Optimus backend (single attempt).
     *
     * @param {Object} options - Request options
     * @param {string} options.pageType - The BFF page type
     * @param {string} options.traceId - The trace ID for tracking
     * @returns {Promise<{parameters: Object, hurraInstanceId: string}|null>} The Optimus response or null
     */
    async fetchOptimusData({pageType, traceId}) {
        this.attemptCount += 1;

        const params = new URLSearchParams({
            bffPageType: pageType,
            traceId: traceId,
        });

        const fetchUrl = `${URL.toAbsolute(OWA_API_URL)}?${params.toString()}`;

        try {
            const response = await fetchWithCSRF(fetchUrl, {
                method: 'GET',
                headers: {'Content-Type': 'application/json'},
            });

            if (!response.ok) {
                Logger.debug('Optimus API returned non-OK response', {status: response.status});
                return null;
            }

            return await response.json();
        } catch (error) {
            Logger.debug(`Unable to fetch Optimus parameters (attempt ${this.attemptCount}/${this.maxAttempts})`, error);
            return null;
        }
    }

    /**
     * Fetches Optimus data with automatic retry logic.
     *
     * @param {Object} options - Request options
     * @param {Function} options.onFetch - Function that performs the actual fetch (returns Promise<boolean>)
     * @param {Function} options.onMaxAttemptsReached - Callback when max attempts are reached
     * @returns {Promise<void>}
     */
    async fetchWithRetry({onFetch, onMaxAttemptsReached = null}) {
        // Skip if a request is already in flight to avoid firing parallel calls
        // when the backend responds slower than the polling interval.
        if (this.isFetching) {
            return;
        }

        // Check max attempts
        if (this.hasReachedMaxAttempts()) {
            this.stopPolling();
            Logger.debug('Max attempts reached for fetching Optimus parameters');
            if (onMaxAttemptsReached) {
                onMaxAttemptsReached();
            }
            return;
        }

        // Note: attemptCount is incremented in fetchOptimusData to avoid double counting
        this.isFetching = true;
        try {
            const success = await onFetch();

            if (success) {
                this.stopPolling();
            }
        } finally {
            this.isFetching = false;
        }
    }

    /**
     * Starts polling for Optimus data with automatic retry logic.
     *
     * @param {Object} options - Request options
     * @param {Function} options.onFetch - Function that performs the actual fetch (returns Promise<boolean>)
     * @param {Function} options.onMaxAttemptsReached - Callback when max attempts are reached
     */
    startPolling({onFetch, onMaxAttemptsReached = null}) {
        // Stop any existing polling
        this.stopPolling();

        // Fetch immediately
        this.fetchWithRetry({onFetch, onMaxAttemptsReached});

        // Set up interval for retries
        this.intervalId = setInterval(() => {
            this.fetchWithRetry({onFetch, onMaxAttemptsReached});
        }, this.retryInterval);
    }

    /**
     * Returns true when an Optimus request is already in flight.
     *
     * @returns {boolean}
     */
    hasPendingRequest() {
        return Boolean(this.pendingPromise);
    }

    /**
     * Returns a promise for the latest resolved result if available; otherwise starts a new request.
     * If a request is in flight, returns the in-flight promise.
     *
     * @param {Object} options - Request options
     * @param {string} options.pageType - The BFF page type
     * @param {string} options.traceId - The trace ID for tracking
     * @returns {Promise<{parameters: Object, hurraInstanceId: string}|null>}
     */
    getLatestResultOrPromise({pageType, traceId}) {
        if (this.pendingPromise) {
            return this.pendingPromise;
        }

        if (this.latestResult) {
            return Promise.resolve(this.latestResult);
        }

        return this.request({pageType, traceId});
    }

    /**
     * Starts a single Optimus request flow and returns a promise that resolves
     * with data or null after max attempts.
     *
     * If a request is already in flight, returns the same promise.
     *
     * @param {Object} options - Request options
     * @param {string} options.pageType - The BFF page type
     * @param {string} options.traceId - The trace ID for tracking
     * @returns {Promise<{parameters: Object, hurraInstanceId: string}|null>}
     */
    request({pageType, traceId}) {
        if (this.pendingPromise) {
            return this.pendingPromise;
        }

        this.resetAttempts();

        this.pendingPromise = new Promise((resolve) => {
            const resolveOnce = (data) => {
                this.stopPolling();
                this.pendingPromise = null;
                this.latestResult = data ?? null;
                resolve(data ?? null);
            };

            this.startPolling({
                onFetch: async () => {
                    const data = await this.fetchOptimusData({pageType, traceId});
                    if (data) {
                        resolveOnce(data);
                        return true;
                    }
                    return false;
                },
                onMaxAttemptsReached: () => {
                    resolveOnce(null);
                }
            });
        });

        return this.pendingPromise;
    }
}

export default OptimusService;

